/**
 * "…the trail from entry to exit" — where each person went, in the order they went there.
 *
 * ─── WHAT THE ENDS MEAN ───
 * The first stop is the entry and the last the exit WITHIN THE QUERY WINDOW. That is not a
 * turnstile crossing, and the difference matters: a window that starts at noon cannot see a
 * morning arrival, so the "entry" it reports is simply the earliest sighting it was allowed to
 * look at. Labelled and caveated rather than left to be assumed, because "entered at 12:04" is a
 * claim about the building and this is a claim about the search.
 *
 * ─── A RETURN VISIT IS ITS OWN STOP ───
 * The server sessionizes by place and by gap, so lobby → server room → lobby is three stops and
 * not two. That is the reason this aggregate exists at all: the set of places someone visited is
 * something the "which areas" answer already gives, and the ORDER is what a trail adds.
 */
import { ArrowRightIcon, UserIcon } from '@heroicons/react/24/outline';
import type { SearchJourneyRow, SearchResult } from '../../../api/contracts';
import {
  AnswerEmpty, AnswerShell, Caveat, Figure, fmtDateTime, fmtDuration, fmtTime,
  type PlaceNamer,
} from './common';

export interface JourneyAnswerProps {
  result: SearchResult;
  namePlace: PlaceNamer;
  /** Narrow the search to one subject, to follow them alone. */
  onPickSubject?: (row: SearchJourneyRow) => void;
}

function Trail({ row, namePlace }: { row: SearchJourneyRow; namePlace: PlaceNamer }) {
  return (
    <ol className="flex flex-wrap items-stretch gap-1.5">
      {row.stops.map((stop, i) => {
        const isFirst = i === 0;
        const isLast = i === row.stops.length - 1;
        const name = stop.unassigned ? stop.place : namePlace(stop.place, 'camera');
        return (
          <li key={`${stop.place}-${stop.first_seen}`} className="flex items-stretch gap-1.5">
            <div
              className={[
                'flex min-w-[7.5rem] flex-col justify-center rounded border px-2 py-1.5',
                isFirst ? 'border-emerald-500/40 bg-emerald-500/5'
                  : isLast ? 'border-brand-ring bg-brand-lighter'
                    : 'border-border bg-card',
              ].join(' ')}
            >
              {/* Only labelled when there is more than one stop: a single-stop trail is neither
                  an entry nor an exit, it is one sighting, and calling it both would be silly. */}
              {row.stops.length > 1 && (isFirst || isLast) && (
                <span className={`text-[9px] font-semibold uppercase tracking-wide ${isFirst ? 'text-emerald-500' : 'text-brand'}`}>
                  {isFirst ? 'First seen' : 'Last seen'}
                </span>
              )}
              <span className="truncate text-xs font-medium text-foreground" title={name}>{name}</span>
              <span className="tabular-nums text-[10px] text-muted-foreground">
                {fmtTime(stop.first_seen)}
                {stop.dwell_seconds > 0 && ` · ${fmtDuration(stop.dwell_seconds)}`}
              </span>
              <span className="text-[10px] text-muted-foreground">
                {stop.events} {stop.events === 1 ? 'detection' : 'detections'}
              </span>
            </div>
            {!isLast && (
              <ArrowRightIcon className="my-auto h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-label="then" />
            )}
          </li>
        );
      })}
    </ol>
  );
}

export default function JourneyAnswer({ result, namePlace, onPickSubject }: JourneyAnswerProps) {
  const rows = (result.rows || []) as SearchJourneyRow[];

  if (!rows.length) {
    return (
      <AnswerEmpty
        cameraScope={result.camera_scope}
        hint="No tracked subject matched. Fires, falls and people-counts have no subject to follow, so a trail cannot be built from them."
      />
    );
  }

  const totalStops = rows.reduce((n, r) => n + r.stops.length, 0);
  const moved = rows.filter((r) => r.stops.length > 1).length;

  return (
    <AnswerShell
      headline={`${rows.length} ${rows.length === 1 ? 'trail' : 'trails'}, earliest arrival first`}
      sub={`${moved} of ${rows.length} moved between places; the rest were seen in one`}
      figures={(
        <>
          <Figure value={rows.length} label={rows.length === 1 ? 'person followed' : 'people followed'} />
          <Figure value={totalStops} label="stops in total" />
          {typeof result.camera_scope === 'number' && (
            <Figure value={result.camera_scope} label={result.camera_scope === 1 ? 'camera searched' : 'cameras searched'} />
          )}
        </>
      )}
      caveats={(
        <>
          <Caveat>
            <strong className="font-medium text-foreground">First and last are the window&apos;s, not the building&apos;s.</strong>{' '}
            A trail begins at the earliest sighting inside the time window you chose — widen the
            window to see whether someone arrived before it started.
          </Caveat>
          {typeof result.gap_minutes === 'number' && (
            <Caveat>
              Two sightings at the same camera more than {result.gap_minutes} minutes apart are
              counted as two visits, so returning somewhere shows as a second stop.
            </Caveat>
          )}
          <Caveat>
            Someone unidentified is followed by their tracker id, which is unique only within one
            camera — so an unknown person crossing two cameras appears as two trails. Add Face
            recognition under “Detected by” to link them by identity.
          </Caveat>
        </>
      )}
    >
      <div className="space-y-2">
        {rows.map((row) => {
          const span = row.first_seen && row.last_seen
            ? (new Date(row.last_seen).getTime() - new Date(row.first_seen).getTime()) / 1000
            : 0;
          return (
            <div key={row.subject} className="rounded-md border border-border bg-card p-2.5">
              <div className="mb-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                  <UserIcon className="h-3.5 w-3.5 text-muted-foreground" />
                  {row.subject_label || (row.is_known ? 'Known' : 'Unidentified')}
                </span>
                {!row.subject_label && (
                  <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                    no identity — tracked only
                  </span>
                )}
                <span className="text-[11px] text-muted-foreground">
                  {fmtDateTime(row.first_seen)} → {fmtDateTime(row.last_seen)}
                  {span > 0 && ` · ${fmtDuration(span)} across ${row.stops.length} ${row.stops.length === 1 ? 'stop' : 'stops'}`}
                </span>
                {onPickSubject && row.subject_label && (
                  <button
                    type="button"
                    onClick={() => onPickSubject(row)}
                    className="ml-auto text-[11px] text-brand hover:underline"
                  >
                    Follow only this person
                  </button>
                )}
              </div>
              <Trail row={row} namePlace={namePlace} />
            </div>
          );
        })}
      </div>
    </AnswerShell>
  );
}
